import * as React from "react";
import { Button } from "@/components/ui";
import { cn } from "@/lib/utils";

export type HoloButtonProps = Omit<React.ComponentProps<typeof Button>, "variant">;

/**
 * The primary action for AI results: filled with the logo gradient. On hover the gradient shifts
 * slightly toward pink and a faint glow of the same colors appears; loading and disabled behave like
 * Button.
 */
export const HoloButton = React.forwardRef<HTMLButtonElement, HoloButtonProps>(
  ({ className, children, ...props }, ref) => (
    <Button
      ref={ref}
      data-slot="holo-button"
      className={cn(
        "group/holo-button relative isolate border-0 bg-holo-fill bg-[length:130%_100%] bg-left font-semibold text-white",
        "transition-[background-position] duration-300 ease-out hover:bg-right motion-reduce:transition-none",
        // Disabled drops the gradient for a flat neutral fill, so it can't read as the special action;
        // a loading button (also disabled underneath) keeps its colors.
        props.disabled && !props.loading && "bg-none bg-slate-200 text-slate-500 disabled:opacity-100",
        className
      )}
      {...props}
    >
      {children}
      <span
        aria-hidden="true"
        data-slot="holo-button-glow"
        className="pointer-events-none absolute -inset-0.5 -z-10 rounded-[inherit] bg-holo-fill bg-[length:130%_100%] opacity-0 blur-sm transition-opacity duration-300 ease-out group-hover/holo-button:opacity-25 group-disabled/holo-button:opacity-0"
      />
    </Button>
  )
);
HoloButton.displayName = "HoloButton";
